import React, { useState, useRef, useEffect } from 'react';
import { useOasisBackend, BACKEND_URL} from '../../hooks/useOasisBackend';
import type { ChatMessage } from '../../api/OasisBackendApi';
import SearchBar from './SearchBar';
import Message from './Message';
import Markdown from "react-markdown";


interface ChatWindowProps {
  initialMessage?: string;
  resetKey: number;
  messages: ChatMessage[];
  setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
  conversationId: number | null;
  setConversationId: (conversationId: number | null) => void;
  onFirstInteraction?: () => void;
}

// Frontend message interface with strict role types
interface RenderMessage {
  content: string;
  role: 'user' | 'assistant' | 'system';
}

const ChatWindow: React.FC<ChatWindowProps> = ({ messages, setMessages, conversationId, setConversationId, initialMessage, resetKey, onFirstInteraction }) => {
  const db = useOasisBackend();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [currentModel, setCurrentModel] = useState('gpt-4o-mini');
  const chatRef = useRef<HTMLDivElement>(null);
  const hasHandledReset = useRef<number | null>(null);
  const hasTrackedFirstInteraction = useRef(false);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const [bottomPadding, setBottomPadding] = useState(0);

  // Convert backend messages to frontend render messages
  const renderMessages: RenderMessage[] = messages.map(msg => ({
    content: msg.content,
    role: msg.role as 'user' | 'assistant' | 'system'
  }));

  // Scroll to position the last user message at the top of the viewport
  useEffect(() => {
    if (shouldScroll && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const lastUserMessage = container.querySelector('.justify-end');
      
      if (lastUserMessage) {
        // 1. Measure chat window height
        const containerHeight = container.clientHeight;
        console.log("Container Height", containerHeight);
        
        // 2. Measure last user message height
        const messageHeight = lastUserMessage.clientHeight;
        console.log("Message Height", messageHeight);
        
        // 3. Calculate and set padding (subtract 16px for message spacing)
        const padding = containerHeight - messageHeight - 32;
        setBottomPadding(padding);
        console.log("Padding", padding);

        // Wait for padding to be rendered before scrolling
        setTimeout(() => {
          // 4. Scroll to bottom to show padding and message
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });
        }, 100);
      }
      setShouldScroll(false);
    }
  }, [messages, shouldScroll]);

  // Scroll to bottom and remove padding when message is complete
  useEffect(() => {
    if (shouldScrollToBottom && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      
      // 6. Remove padding
      setBottomPadding(0);
      
      // 7. Scroll to bottom
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
      
      setShouldScrollToBottom(false);
    }
  }, [messages, shouldScrollToBottom]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded) {
      // Focus input when expanded
    }
  }, [isExpanded]);

  // Outside click handler
  useEffect(() => {
    if (!isExpanded) return;
    function handleClickOutside(event: MouseEvent) {
      if (chatRef.current && !chatRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded]);

  // Reset chat when resetKey changes
  useEffect(() => {
    if (resetKey !== hasHandledReset.current) {
      hasHandledReset.current = resetKey;
      setMessages([]);
      setConversationId(null);
      setIsExpanded(false);
      hasTrackedFirstInteraction.current = false;
      if (initialMessage) {
        console.log("Initial Message", initialMessage, resetKey);
        handleSendMessage(initialMessage);
      }
    }
    // eslint-disable-next-line
  }, [resetKey]);

  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;

    // Track first interaction if this is not the initial auto-message and we haven't tracked yet
    if (!hasTrackedFirstInteraction.current && onFirstInteraction && message !== initialMessage) {
      hasTrackedFirstInteraction.current = true;
      onFirstInteraction();
    }

    setIsLoading(true);
    setMessages((prev: ChatMessage[]) => [...prev, { role: 'user', content: message }]);
    console.log("Send Message",message);
    setShouldScroll(true);

    const response = await db.endpoints.rhea.rheaApiChatCreate(
      {
        message,
        conversation_id: conversationId || undefined,
        model: currentModel,
        stream: true,
      }
    )

    setIsLoading(false);
    setShouldScrollToBottom(true);
  };

  const getLastAssistantMessage = () => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') {
        return messages[i].content;
      }
    }
    return 'No response yet';
  };

  return (
    <div 
      ref={chatRef}
      className={`rounded-lg shadow-lg transition-all duration-300 ease-in-out hover:border border-none ${
        isExpanded 
          ? 'w-full h-[600px] mb-8' 
          : 'w-full h-24 cursor-pointer hover:shadow-xl'
      }`}
      style={{ 
        backgroundColor: 'var(--rhea-bg-primary)',
        border: '1px solid var(--rhea-card-border)'
      }}
      onClick={() => !isExpanded && setIsExpanded(true)}
    >
      {/* Content */}
      <div className="h-full flex flex-col">
        {isExpanded ? (
          <>
            {/* Messages */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4"
            >
              {renderMessages.map((message, index) => (
                <Message
                  key={index}
                  content={message.content}
                  role={message.role}
                  isCollapsed={false}
                />
              ))}
              <div ref={messagesEndRef} />
              {/* Dynamic padding */}
              <div style={{ height: bottomPadding }} />
            </div>

            {/* Input Form */}
            <div className="p-4" style={{ borderTop: '1px solid var(--rhea-card-border)' }}>
              <SearchBar
                onSearch={handleSendMessage}
                isLoading={isLoading}
                placeholderMessage="Type your message..."
                autoFocus={true}
                deleteOnSubmit={true}
              />
            </div>
          </>
        ) : (
          // Show last message in a proper message bubble when collapsed
          <div className="p-4">
            <Message
              content={getLastAssistantMessage()}
              role={'assistant' as const}
              isCollapsed={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatWindow; 